import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, CardActions, Typography, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, WhatsApp, Facebook, CardGiftcard, Favorite, FavoriteBorder, ThumbUp, ThumbDown } from '@mui/icons-material';
import NameCertificate from './NameCertificate';

const APP_URL = 'https://app.cumalochannel.com';

// Official X (Twitter) logo — MUI ships the old bird, so we use the SVG directly
const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

function NameCard({ name, wordMeanings, meaning, theme, onCopy, isFavorited, onToggleFavorite, favoritesDisabled, feedbackState, onFeedback }) {
  const { t } = useTranslation();
  const [certOpen, setCertOpen] = useState(false);

  const handleShareWhatsApp = () => {
    const shareUrl = `https://cumalochannel.com/nama/?n=${encodeURIComponent(name)}&m=${encodeURIComponent(meaning)}`;
    const text = encodeURIComponent(
      `Saya menemukan nama bayi yang bagus: *${name}*\nArtinya: _${meaning}_\n\nCari nama bayi lainnya di:\n${shareUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleShareX = () => {
    const text = encodeURIComponent(
      `Nama bayi yang indah: "${name}" — ${meaning}\n\nTemukan nama bayi Indonesia di ${APP_URL} #NamaBayi #BayiIndonesia`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent(APP_URL);
    const quote = encodeURIComponent(`Nama bayi yang indah: "${name}" — ${meaning}`);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          {name}
        </Typography>
        {wordMeanings && wordMeanings.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {name.split(' ').map((word, i) => (
              <Typography key={i} variant="caption" color="text.secondary" display="block">
                <strong>{word}</strong>: {wordMeanings[i]}
              </Typography>
            ))}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {meaning}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', pt: 0 }}>
        <Tooltip title="👍">
          <IconButton size="small" onClick={() => onFeedback && onFeedback(true)}
            sx={{ color: feedbackState === 'up' ? 'success.main' : 'text.disabled', transition: 'color 0.2s', '&:hover': { color: 'success.main' } }}>
            <ThumbUp fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="👎">
          <IconButton size="small" onClick={() => onFeedback && onFeedback(false)}
            sx={{ color: feedbackState === 'down' ? 'error.main' : 'text.disabled', transition: 'color 0.2s', '&:hover': { color: 'error.main' } }}>
            <ThumbDown fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={favoritesDisabled && !isFavorited ? t('favoritesLimitTooltip') : isFavorited ? t('unfavoriteTooltip') : t('favoriteTooltip')}>
          <span>
            <IconButton
              size="small"
              onClick={onToggleFavorite}
              disabled={favoritesDisabled && !isFavorited}
              sx={{
                color: isFavorited ? 'error.main' : 'text.disabled',
                transition: 'color 0.2s',
                '&:hover': { color: 'error.main' },
              }}
            >
              {isFavorited ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('shareWhatsAppTooltip')}>
          <IconButton size="small" onClick={handleShareWhatsApp}
            sx={{ color: 'text.disabled', transition: 'color 0.2s', '&:hover': { color: '#25D366' } }}>
            <WhatsApp fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('shareXTooltip')}>
          <IconButton size="small" onClick={handleShareX}
            sx={{ color: 'text.disabled', transition: 'color 0.2s', '&:hover': { color: '#000' } }}>
            <XIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('shareFacebookTooltip')}>
          <IconButton size="small" onClick={handleShareFacebook}
            sx={{ color: 'text.disabled', transition: 'color 0.2s', '&:hover': { color: '#1877F2' } }}>
            <Facebook fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('copyTooltip')}>
          <IconButton size="small" onClick={onCopy}
            sx={{ color: 'text.disabled', transition: 'color 0.2s', '&:hover': { color: 'primary.main' } }}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('certificateButton')}>
          <IconButton size="small" onClick={() => setCertOpen(true)}
            sx={{ color: 'text.disabled', transition: 'color 0.2s', '&:hover': { color: 'warning.main' } }}>
            <CardGiftcard fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>

      <NameCertificate
        open={certOpen}
        onClose={() => setCertOpen(false)}
        name={name}
        meaning={meaning}
        theme={theme}
      />
    </Card>
  );
}

export default NameCard;
